// ProjectForm — the single source of truth for project definition.
//
// Two modes, one form:
//   • mode="create" starts empty, writes a new project (status="draft"),
//     then navigates to the new project's detail page.
//   • mode="edit"   starts populated from an existing project, PATCHes
//     the same project id, then navigates back to Project Detail.
//
// Field definitions, suggestion lists, validation rules, and the 4-step
// wizard flow live here — never in a page. Any future addition to
// project definition happens here and both modes get it for free.

import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, X, Plus, ArrowRight, Check, Minus } from "lucide-react";
import { useCommons } from "../lib/store";
import { createProject, updateProject } from "../lib/api";
import { CATEGORIES, CATEGORY_BY_ID } from "../lib/categories";

const STEPS = ["What", "When", "Where", "Needs"];
const EMOJI = ["🌳","🛠","🌱","📚","🚲","🍲","☕","🎨","🎵","🧺","🐝","⛺","🧹","🏕","🌻","🥁"];
const PEOPLE_SUGG = [
  { label: "Volunteer", needed: 10 },
  { label: "Driver", needed: 2 },
  { label: "Photographer", needed: 1 },
  { label: "Cook", needed: 3 },
  { label: "Setup crew member", needed: 4 },
  { label: "Cleanup crew member", needed: 3 },
];
const THING_SUGG = [
  { label: "Truck", needed: 1, emoji: "🚚" },
  { label: "Shovel", needed: 6, emoji: "🪣" },
  { label: "Ladder", needed: 2, emoji: "🪜" },
  { label: "Lunch", needed: 10, emoji: "🥪" },
  { label: "Coffee sponsor", needed: 1, emoji: "☕" },
  { label: "First aid kit", needed: 1, emoji: "🩹" },
  { label: "Venue", needed: 1, emoji: "🏛" },
  { label: "Trash bag", needed: 20, emoji: "🗑" },
];

// Normalize an existing project's requirements into the shape the form
// tracks. Preserves every field the wizard needs; anything else is left
// alone by construction because we PATCH only the fields the form owns.
function reqsFromProject(project) {
  return (project?.requirements || []).map((r) => ({
    id: r.id,
    kind: r.kind,
    label: r.label,
    needed: r.needed,
    emoji: r.emoji,
    // Preserve the original label so a rename inside edit mode doesn't
    // orphan pledges that pointed at the old wording; not currently
    // used but explicit here as intent for a future migration.
    pluralLabel: r.pluralLabel,
    shortDescription: r.shortDescription || "",
    description: r.description || "",
  }));
}

// Optional per-role/Thing descriptions; trimmed and omitted when empty.
const descFields = ({ shortDescription, description }) => ({
  ...(shortDescription && shortDescription.trim() ? { shortDescription: shortDescription.trim() } : {}),
  ...(description && description.trim() ? { description: description.trim() } : {}),
});
export const SHORT_DESC_MAX = 120;
export const FULL_DESC_MAX = 1000;

export default function ProjectForm({ mode, project, testIdPrefix = "" }) {
  const nav = useNavigate();
  const location = useLocation();
  const { user, refresh } = useCommons();
  const isEdit = mode === "edit";

  const [step, setStep] = useState(0);
  const [emoji, setEmoji] = useState(project?.emoji || "🌳");
  const [title, setTitle] = useState(project?.title || "");
  const [purpose, setPurpose] = useState(project?.purpose || "");
  const [category, setCategory] = useState(
    project?.category && CATEGORY_BY_ID[project.category] ? project.category : ""
  );
  const [when, setWhen] = useState(project?.when || "");
  const [commitBy, setCommitBy] = useState(project?.commitBy || "");
  const [where, setWhere] = useState(project?.where || "");
  const [reqs, setReqs] = useState(() => reqsFromProject(project));
  const [custom, setCustom] = useState("");
  const [customKind, setCustomKind] = useState("people");
  const [busy, setBusy] = useState(false);

  const canGo =
    (step === 0 && title.trim().length > 1 && purpose.trim().length > 4 && !!category) ||
    (step === 1 && when.trim() && commitBy.trim()) ||
    (step === 2 && where.trim()) ||
    (step === 3 && reqs.length > 0);

  const add = (kind, label, needed, e) => {
    if (reqs.some((r) => r.label.toLowerCase() === label.toLowerCase())) return;
    setReqs([...reqs, { id: `r-${Date.now()}-${reqs.length}`, kind, label, needed, emoji: e, shortDescription: "", description: "" }]);
  };
  const addCustom = () => {
    const label = custom.trim();
    if (!label) return;
    add(customKind, label, customKind === "people" ? 5 : 1);
    setCustom("");
  };
  const remove = (id) => setReqs(reqs.filter((r) => r.id !== id));
  const bump = (id, d) =>
    setReqs(reqs.map((r) => (r.id === id ? { ...r, needed: Math.max(1, r.needed + d) } : r)));
  const setDescription = (id, field, text) =>
    setReqs(reqs.map((r) => (r.id === id ? { ...r, [field]: text } : r)));
  // Ids whose description panel is currently open; ones with text start open.
  const [openDetails, setOpenDetails] = useState(
    () => new Set(reqs.filter((r) => (r.description || "").trim() || (r.shortDescription || "").trim()).map((r) => r.id))
  );
  const toggleDetails = (id) => {
    setOpenDetails((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Where do the primary and secondary actions go? Both are decided
  // once, up here, so the sticky footer at the bottom stays declarative.
  // We thread `location.state` on the return so that a visitor who
  // opened the editor from a Category page still lands back on that
  // category's Detail context (not on plain Home).
  const exit = () => {
    if (isEdit) nav(`/project/${project.id}`, { state: location.state || undefined });
    else nav(-1);
  };

  const submit = async () => {
    if (busy) return;
    if (isEdit) {
      setBusy(true);
      try {
        const patch = {
          emoji,
          title: title.trim(),
          purpose: purpose.trim(),
          category,
          when: when.trim(),
          commitBy: commitBy.trim() || undefined,
          where: where.trim(),
          requirements: reqs.map(({ id, kind, label, needed, emoji: e, pluralLabel, ...rest }) => ({
            id, kind, label, needed, emoji: e,
            ...(pluralLabel ? { pluralLabel } : {}),
            ...descFields(rest),
          })),
        };
        await updateProject(project.id, patch);
        await refresh();
        nav(`/project/${project.id}`, { state: location.state || undefined });
      } finally {
        setBusy(false);
      }
      return;
    }
    if (!user) return;
    setBusy(true);
    try {
      const newProject = {
        id: `p-${Date.now()}`,
        emoji,
        title: title.trim(),
        purpose: purpose.trim(),
        category,
        when: when.trim(),
        commitBy: commitBy.trim() || undefined,
        where: where.trim(),
        stewardId: user.personId,
        coStewardIds: [],
        requirements: reqs.map(({ id, kind, label, needed, emoji: e, ...rest }) => ({
          id, kind, label, needed, emoji: e,
          ...descFields(rest),
        })),
        status: "draft",
        createdAt: Date.now(),
      };
      await createProject(newProject);
      await refresh();
      nav(`/project/${newProject.id}`, { replace: true });
    } finally {
      setBusy(false);
    }
  };

  const next = () => (step < STEPS.length - 1 ? setStep(step + 1) : submit());
  const back = () => (step > 0 ? setStep(step - 1) : exit());

  const finalLabel = useMemo(() => {
    if (busy) return "Saving…";
    return isEdit ? "Save changes" : "Save draft & finish";
  }, [busy, isEdit]);

  const tid = (name) => (testIdPrefix ? `${testIdPrefix}-${name}` : name);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100dvh" }}>
      <div className="c-wizard-head">
        <button className="c-icon-btn" onClick={back} data-testid={tid("wizard-back-btn")}>
          {step === 0 ? <X size={20} /> : <ArrowLeft size={20} />}
        </button>
        <div className="c-step-dots">
          {STEPS.map((_, i) => (
            <div key={i} className={`c-step-dot ${i === step ? "active" : ""} ${i < step ? "done" : ""}`} />
          ))}
        </div>
        {/* Explicit Cancel affordance in edit mode so leaving without
            saving is unambiguous even mid-wizard. In create mode the
            X icon on step 0 already handles this. */}
        {isEdit ? (
          <button
            className="c-icon-btn"
            onClick={exit}
            data-testid={tid("wizard-cancel-btn")}
            aria-label="Cancel and return without saving"
          >
            <X size={20} />
          </button>
        ) : (
          <div style={{ width: 40 }} />
        )}
      </div>

      <div style={{ padding: 20, flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="c-eyebrow">
          {isEdit ? "Edit project" : "Step"} {step + 1} of {STEPS.length} · {STEPS[step]}
        </div>

        {step === 0 && (
          <div className="flex-col gap-4">
            <h2 className="c-h1" style={{ fontSize: 28 }}>
              {isEdit ? "What are you changing?" : "What are you starting?"}
            </h2>
            <div className="c-body">Something concrete a group of neighbors could actually do together.</div>

            <div className="c-label">Pick an icon</div>
            <div className="c-emoji-grid">
              {EMOJI.map((e) => (
                <button key={e} className={`c-emoji-chip ${emoji === e ? "active" : ""}`} onClick={() => setEmoji(e)} data-testid={tid(`emoji-${e}`)}>{e}</button>
              ))}
            </div>

            <input className="c-input" placeholder="Clean Maple Creek" value={title} onChange={(e) => setTitle(e.target.value)} data-testid={tid("input-title")} />

            <div className="c-label">Why does it matter?</div>
            <textarea className="c-input c-input-tall" placeholder="Pick up trash before spring rains carry it downstream."
              value={purpose} onChange={(e) => setPurpose(e.target.value)} data-testid={tid("input-purpose")} />

            {/* Category picker — a project's discovery lens.
                Not a wizard step; just the sixth field of Step 1. Six
                compact chips so a steward picks with one glance rather
                than choosing from a dropdown. Required for both create
                and edit. Does not alter mechanics. */}
            <div className="c-label">Which door does this belong behind?</div>
            <div className="c-cat-picker" data-testid={tid("category-picker")}>
              {CATEGORIES.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`c-cat-chip ${category === c.id ? "active" : ""}`}
                  onClick={() => setCategory(c.id)}
                  style={{
                    "--cat-accent": c.accent,
                    "--cat-surface": c.surface,
                    "--cat-ink": c.ink,
                  }}
                  data-testid={tid(`category-chip-${c.id}`)}
                  aria-pressed={category === c.id}
                >
                  <span className="c-cat-chip-emoji" aria-hidden="true">{c.emoji}</span>
                  <span className="c-cat-chip-name">{c.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="flex-col gap-4">
            <h2 className="c-h1" style={{ fontSize: 28 }}>When?</h2>
            <div className="c-body">Two dates. When's the deadline for people to commit, and when the mission actually happens.</div>
            <div className="c-label">📅 Commit by</div>
            <input className="c-input" placeholder="Wed, Jun 19" value={commitBy} onChange={(e) => setCommitBy(e.target.value)} data-testid={tid("input-commit-by")} />
            <div className="c-label">🎉 Happens</div>
            <input className="c-input" placeholder="Sat, Jun 22 · 9:00 AM" value={when} onChange={(e) => setWhen(e.target.value)} data-testid={tid("input-when")} />
          </div>
        )}

        {step === 2 && (
          <div className="flex-col gap-4">
            <h2 className="c-h1" style={{ fontSize: 28 }}>Where?</h2>
            <div className="c-body">A general location is enough.</div>
            <input className="c-input" placeholder="Maple Creek trailhead" value={where} onChange={(e) => setWhere(e.target.value)} data-testid={tid("input-where")} />
          </div>
        )}

        {step === 3 && (
          <div className="flex-col gap-4">
            <h2 className="c-h1" style={{ fontSize: 28 }}>What's needed to make it real?</h2>
            <div className="c-body">Two kinds of capacity. People show up. Things get pledged.</div>

            <div className="c-eyebrow">People</div>
            <div className="c-chip-row">
              {PEOPLE_SUGG.map((s) => {
                const active = reqs.some((r) => r.label.toLowerCase() === s.label.toLowerCase());
                return (
                  <button key={s.label} className={`c-suggest ${active ? "active" : ""}`}
                    onClick={() => active ? remove(reqs.find((r) => r.label === s.label).id) : add("people", s.label, s.needed)}
                    data-testid={tid(`suggest-people-${s.label}`)}>
                    {active ? <Check size={14} /> : <Plus size={14} />} {s.label}
                  </button>
                );
              })}
            </div>

            <div className="c-eyebrow">Things</div>
            <div className="c-chip-row">
              {THING_SUGG.map((s) => {
                const active = reqs.some((r) => r.label.toLowerCase() === s.label.toLowerCase());
                return (
                  <button key={s.label} className={`c-suggest ${active ? "active" : ""}`}
                    onClick={() => active ? remove(reqs.find((r) => r.label === s.label).id) : add("thing", s.label, s.needed, s.emoji)}
                    data-testid={tid(`suggest-thing-${s.label}`)}>
                    <span>{s.emoji}</span> {s.label}
                  </button>
                );
              })}
            </div>

            <div className="c-eyebrow">Or add your own</div>
            <div className="c-chip-row">
              <button className={`c-kind-chip ${customKind === "people" ? "active" : ""}`} onClick={() => setCustomKind("people")} data-testid={tid("kind-people")}>People</button>
              <button className={`c-kind-chip ${customKind === "thing" ? "active" : ""}`} onClick={() => setCustomKind("thing")} data-testid={tid("kind-thing")}>Thing</button>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input className="c-input" style={{ flex: 1, marginBottom: 0 }}
                placeholder={customKind === "people" ? "e.g. Route lead" : "e.g. Wheelbarrows"}
                value={custom} onChange={(e) => setCustom(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addCustom()}
                data-testid={tid("input-custom-req")} />
              <button className="c-btn c-btn-primary" style={{ padding: "0 18px" }} onClick={addCustom} data-testid={tid("add-custom-btn")}><Plus size={20} /></button>
            </div>

            {reqs.length > 0 && (
              <div className="flex-col gap-4">
                <div className="c-label">Your list ({reqs.length})</div>
                {reqs.map((r) => {
                  const hasDetails = !!((r.description && r.description.trim()) || (r.shortDescription && r.shortDescription.trim()));
                  const detailsOpen = openDetails.has(r.id);
                  return (
                    <div key={r.id} data-testid={tid(`draft-req-${r.id}`)}>
                      <div className="c-draft-row">
                        <div className="c-draft-badge">{r.kind === "people" ? "👥" : (r.emoji ?? "📦")}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700 }}>{r.label}</div>
                          <div className="c-helper" style={{ marginTop: 0 }}>
                            <span>{r.kind === "people" ? "People" : "Thing"}</span>
                            <span> · </span>
                            <button
                              type="button"
                              className="c-req-details-toggle"
                              onClick={() => toggleDetails(r.id)}
                              aria-expanded={detailsOpen}
                              data-testid={tid(`draft-req-${r.id}-details-toggle`)}
                            >
                              {detailsOpen ? (hasDetails ? "Details \u25B4" : "Close \u25B4") : (hasDetails ? "Details \u25BE" : "Add details \u25BE")}
                            </button>
                          </div>
                        </div>
                        <button className="c-stepper" onClick={() => bump(r.id, -1)} data-testid={tid(`req-minus-${r.id}`)}><Minus size={16} /></button>
                        <div style={{ fontWeight: 700, minWidth: 22, textAlign: "center" }}>{r.needed}</div>
                        <button className="c-stepper" onClick={() => bump(r.id, 1)} data-testid={tid(`req-plus-${r.id}`)}><Plus size={16} /></button>
                        <button className="c-stepper" onClick={() => remove(r.id)} data-testid={tid(`req-remove-${r.id}`)}>
                          <X size={16} color="var(--c-brand-primary)" />
                        </button>
                      </div>
                      {detailsOpen ? (
                        <div className="c-req-details-panel">
                          <label className="c-label c-req-desc-label" htmlFor={`sdesc-${r.id}`}>
                            Short description <span>(optional)</span>
                            <span className="c-req-desc-count">{(r.shortDescription || "").length}/{SHORT_DESC_MAX}</span>
                          </label>
                          <input
                            id={`sdesc-${r.id}`}
                            className="c-input c-req-desc-input"
                            placeholder="One line on what saying yes involves"
                            value={r.shortDescription || ""}
                            onChange={(e) => setDescription(r.id, "shortDescription", e.target.value)}
                            maxLength={SHORT_DESC_MAX}
                            data-testid={tid(`draft-req-${r.id}-short-description`)}
                          />
                          <label className="c-label c-req-desc-label" htmlFor={`desc-${r.id}`}>
                            More details <span>(optional)</span>
                            <span className="c-req-desc-count">{(r.description || "").length}/{FULL_DESC_MAX}</span>
                          </label>
                          <textarea
                            id={`desc-${r.id}`}
                            className="c-input c-textarea c-req-desc-input"
                            placeholder="Time expectations, useful skills, instructions, or a checklist."
                            value={r.description || ""}
                            onChange={(e) => setDescription(r.id, "description", e.target.value)}
                            maxLength={FULL_DESC_MAX}
                            rows={3}
                            data-testid={tid(`draft-req-${r.id}-description`)}
                          />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="c-sticky-footer">
        <button className="c-btn c-btn-primary" onClick={next} disabled={!canGo || busy} style={{ width: "100%" }} data-testid={tid("wizard-next-btn")}>
          {step === STEPS.length - 1 ? finalLabel : "Continue"}
          {step === STEPS.length - 1 ? <Check size={18} /> : <ArrowRight size={18} />}
        </button>
      </div>
    </div>
  );
}
